import {Typography, Box, Card, Divider, Chip, Button, Tooltip} from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import UpdateIcon from '@mui/icons-material/Update';
import AttachFileIcon from "@mui/icons-material/AttachFile";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import moment from 'moment-timezone';
import {useTranslation} from 'react-i18next';

import {DATE_FORMAT_IN_COMPACT_READABLE_FORMAT} from '../../config/DateTimeConstants';
import {AMENDMENT_ATTACHMENT_SCOPES} from '../../domain/AmendmentConstants';
import {AMENDMENT_DIRECTION} from '../../helpers/ShiftHelper';

const styles = {
    card: {
        container: {
            borderRadius: '0.75rem',
            overflow: 'hidden',
        },
        content: {
            padding: '0.5rem',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            padding: '0.5rem'
        },
        headerLeftGroup: {
            alignItems: 'center',
            display: 'flex',
            gap: '0.75rem',
        },
        headerCredit: {
            backgroundColor: '#EAF7EE',
        },
        headerDebit: {
            backgroundColor: '#FDEEEE',
        },
        metadataRow: {
            display: 'flex',
            justifyContent: 'space-between',
        },
        note: {
            color: 'text.secondary',
            fontStyle: 'italic',
        },
        titleRow: {
            alignItems: 'center',
            display: 'flex',
            gap: '0.5rem',
        },
        deleteButton: {
            color: 'grey.600',
            borderColor: 'grey.500',
            backgroundColor: 'white',
            '&:hover': {
                backgroundColor: 'grey.200'
            }
        },
        divider: {
            margin: '0.75rem'
        },
        attachmentIcon: {
            transform: 'rotate(90deg)'
        }
    },
};

const buildAmendmentCardDetails = (amendment, timezone) => ({
    isAmendmentCredit: amendment.direction === AMENDMENT_DIRECTION.CREDIT,
    amendmentType: amendment.amendmentType,
    amendmentDurationInMinutes: amendment.durationInMinutes ?? 0,
    incidentTime: moment(amendment.startTime).tz(timezone).format(DATE_FORMAT_IN_COMPACT_READABLE_FORMAT),
    addedBy: amendment?.createdBy || '-',
    addedOn: moment(amendment.created).tz(timezone).format(DATE_FORMAT_IN_COMPACT_READABLE_FORMAT),
    note: amendment?.notes,
});

const hasAttachedAmendment = (amendment) => amendment?.shift?.id != null;

const buildAttachmentDetails = (amendment) => {
    let attachmentDetails = {
        scope: AMENDMENT_ATTACHMENT_SCOPES.STANDALONE,
        icon: <AddIcon/>
    };

    if (hasAttachedAmendment(amendment)) {
        attachmentDetails = {
            scope: AMENDMENT_ATTACHMENT_SCOPES.ATTACHED,
            icon: <AttachFileIcon sx={styles.card.attachmentIcon}/>
        };
    }

    return attachmentDetails;
};

export default function AmendmentCard({amendment, shiftTimeRange, servicesLabel, timezone, isActionable = false, onDeleteAmendment
}) {
    const {t} = useTranslation();
    const amendmentCardDetails = buildAmendmentCardDetails(amendment, timezone);
    const headerBackground = amendmentCardDetails.isAmendmentCredit ? styles.card.headerCredit : styles.card.headerDebit;
    const amendmentDirectionSign = amendmentCardDetails.isAmendmentCredit ? '+' : '-';
    const attachmentDetails = buildAttachmentDetails(amendment);

    return (
        <Card variant="outlined" sx={styles.card.container}>
            <Box sx={[styles.card.header, headerBackground]}>
                {isActionable
                    ?
                    <>
                        <Box sx={styles.card.headerLeftGroup}>
                            <Box sx={styles.card.titleRow}>
                                <Tooltip title={t(`shiftAdminAddAmendmentDialog.form.scope.${attachmentDetails.scope}.tooltip`)} arrow>
                                    <Chip size="small" icon={attachmentDetails.icon} label={t(`shiftAdminAddAmendmentDialog.form.scope.${attachmentDetails.scope}.label`)}/>
                                </Tooltip>

                                <Typography fontWeight={700}>
                                    {t(`shiftAdminAddAmendmentDialog.form.amendmentTypes.${amendmentCardDetails.amendmentType}`)}
                                </Typography>
                            </Box>

                            <Typography fontWeight={700} color={amendmentCardDetails.isAmendmentCredit ? 'success.main' : 'error.main'}>
                                {amendmentDirectionSign}{amendmentCardDetails.amendmentDurationInMinutes} {t("shiftAdminAddAmendmentDialog.form.durationUnitMinutes")}
                            </Typography>
                        </Box>

                        <Button
                            variant="outlined"
                            size="small"
                            sx={styles.card.deleteButton}
                            startIcon={<DeleteIcon/>}
                            onClick={() => onDeleteAmendment?.(amendment)}>
                            {t("common.deleteLabel")}
                        </Button>
                    </>
                    :
                    <>
                        <Box sx={styles.card.titleRow}>
                            {
                                amendmentCardDetails.isAmendmentCredit
                                ? <UpdateIcon color="success"/>
                                : <RestoreIcon color="error"/>
                            }

                            <Typography fontWeight={700}>
                                {t(`shiftAdminAddAmendmentDialog.form.amendmentTypes.${amendmentCardDetails.amendmentType}`)}
                            </Typography>
                        </Box>

                        <Typography fontWeight={700} color={amendmentCardDetails.isAmendmentCredit ? 'success.main' : 'error.main'}>
                            {amendmentDirectionSign}{amendmentCardDetails.amendmentDurationInMinutes} {t("shiftAdminAddAmendmentDialog.form.durationUnitMinutes")}
                        </Typography>
                    </>
                }
            </Box>

            <Box sx={styles.card.content}>
                {amendment?.shift?.id
                    &&
                    (
                        <Box sx={styles.card.metadataRow}>
                            <Typography color="text.secondary">
                                {t("shiftAdminPerformerAmendmentsDialog.card.shift")}
                            </Typography>

                            <Typography fontWeight={600}>
                                {shiftTimeRange}{servicesLabel}
                            </Typography>
                        </Box>
                    )}

                <Box sx={styles.card.metadataRow}>
                    <Typography color="text.secondary">
                        {t("shiftAdminAddAmendmentDialog.form.incidentTime")}
                    </Typography>

                    <Typography fontWeight={600}>
                        {amendmentCardDetails.incidentTime}
                    </Typography>
                </Box>
                {
                    isActionable
                    &&
                    <>
                        <Box sx={styles.card.metadataRow}>
                            <Typography color="text.secondary">
                                {t("shiftAdminPerformerAmendmentsDialog.card.addedBy")}
                            </Typography>

                            <Typography fontWeight={600}>
                                {amendmentCardDetails.addedBy}
                            </Typography>
                        </Box>
                        <Box sx={styles.card.metadataRow}>
                            <Typography color="text.secondary">
                                {t("shiftAdminPerformerAmendmentsDialog.card.addedOn")}
                            </Typography>

                            <Typography fontWeight={600}>
                                {amendmentCardDetails.addedOn}
                            </Typography>
                        </Box>
                    </>
                }

                {
                    amendmentCardDetails.note
                    &&
                    <>
                        <Divider sx={styles.card.divider}/>
                        <Typography sx={styles.card.note}>
                            "{amendmentCardDetails.note}"
                        </Typography>
                    </>
                }
            </Box>
        </Card>
    );
}
